import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowRight, Plus, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const field = "h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-emerald-500 text-sm";
const labelClass = "text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1.5 block";

export const MemberFormPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    gender: "",
    birth_date: "",
    birth_place: "",
    phone: "",
    address_street: "",
    address_postal_code: "",
    address_city: "",
    profession: "",
    civil_status: "",
    spouse_first_name: "",
    spouse_last_name: "",
    spouse_birth_date: "",
    spouse_phone: "",
    country: "FR",
  });

  const [hasChildren, setHasChildren] = useState("non");
  const [children, setChildren] = useState([
    { first_name: "", last_name: "", birth_date: "" },
  ]);
  const [beneficiaryChoice, setBeneficiaryChoice] = useState("non");
  const [beneficiary, setBeneficiary] = useState({
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [acceptCG, setAcceptCG] = useState(false);

  const parseAddress = (fullAddress) => {
    if (!fullAddress) return { street: "", zip: "", city: "" };
    const parts = fullAddress.split(",");
    const street = parts[0] || "";
    const rest = parts[1] || "";
    const zipMatch = rest.match(/\d{5}/);
    const zip = zipMatch ? zipMatch[0] : "";
    const city = rest.replace(zip, "").trim();
    return { street, zip, city };
  };

  const handleLogout = () => {
    localStorage.removeItem("hafani_member_token");
    navigate("/login");
  };

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const token = localStorage.getItem("hafani_member_token");
        if (!token) { navigate("/login"); return; }

        const res = await axios.get(`${API}/members/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const m = res.data;
        const addr = parseAddress(m.address);

        setForm({
          email: m.email || "",
          first_name: m.first_name || "",
          last_name: m.last_name || "",
          gender: m.gender || "",
          birth_date: m.birth_date || "",
          birth_place: m.birth_place || "",
          phone: m.phone || "",
          address_street: addr.street,
          address_postal_code: addr.zip,
          address_city: addr.city,
          profession: m.profession || "",
          civil_status: m.civil_status || "",
          spouse_first_name: m.spouse_first_name || "",
          spouse_last_name: m.spouse_last_name || "",
          spouse_birth_date: m.spouse_birth_date || "",
          spouse_phone: m.spouse_phone || "",
          country: m.country || "FR",
        });

        if (m.children?.length > 0) {
          setHasChildren("oui");
          setChildren(m.children);
        }
        if (m.beneficiary) {
          setBeneficiaryChoice("oui");
          setBeneficiary(m.beneficiary);
        }
      } catch (e) {
        if (e.response?.status === 401) navigate("/login");
      } finally {
        setFetching(false);
      }
    };
    fetchMember();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChildChange = (index, fieldName, value) => {
    setChildren((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [fieldName]: value };
      return next;
    });
  };

  const handleBeneficiaryChange = (e) => {
    const { name, value } = e.target;
    setBeneficiary((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptCG) {
      toast({
        title: "Conditions générales",
        description: "Merci de valider les conditions générales.",
      });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("hafani_member_token");
      const payload = {
        ...form,
        has_children_under_18: hasChildren === "oui",
        children: hasChildren === "oui" ? children : [],
        beneficiary: beneficiaryChoice === "oui" ? beneficiary : null,
        address: `${form.address_street}, ${form.address_postal_code} ${form.address_city}`.trim(),
        membership_type: "adhesion",
        year: new Date().getFullYear(),
      };

      await axios.put(`${API}/members/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast({ title: "Dossier enregistré", description: "Direction le paiement." });
      navigate("/paiement");
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le profil.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-[#EAE8E4] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EAE8E4] font-sans antialiased flex flex-col">

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200/60 bg-[#EAE8E4]/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex flex-col items-start group">
            <img
              src="/assets/logo.png"
              alt="Hafani"
              className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
              Hafani, une solution pensée pour notre fin
            </span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors group"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-10 lg:py-14">

        {/* Page title */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-2">
            Mon dossier
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight">
            Compléter mon{" "}
            <em className="not-italic text-emerald-700">adhésion.</em>
          </h1>
          <p className="text-slate-400 text-sm font-light mt-3 max-w-md">
            Ces informations sont nécessaires pour votre adhésion à la communauté Hafani.
            Elles restent strictement confidentielles.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-3xl p-7 sm:p-10 shadow-sm border border-slate-100 space-y-10">

            {/* ── 01. Identité ── */}
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6 flex items-center gap-3">
                <span className="font-serif text-slate-200 text-2xl leading-none">01</span>
                Identité
              </p>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="first_name" className={labelClass}>Prénom <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="first_name" name="first_name" value={form.first_name} onChange={handleChange} required className={field} placeholder="Mohamed" />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className={labelClass}>Nom <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="last_name" name="last_name" value={form.last_name} onChange={handleChange} required className={field} placeholder="Ansoir" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className={labelClass}>Email <span className="text-slate-300 normal-case font-normal">(optionnel)</span></Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} className={field} placeholder="exemple@email.com" />
                  </div>
                  <div>
                    <Label className={labelClass}>Sexe <span className="text-red-400 normal-case">*</span></Label>
                    <RadioGroup
                      value={form.gender}
                      onValueChange={(v) => setForm((p) => ({ ...p, gender: v }))}
                      className="flex gap-3 mt-1"
                    >
                      {["Homme", "Femme"].map((g) => (
                        <label
                          key={g}
                          htmlFor={`gender-${g}`}
                          className={`flex-1 flex items-center justify-center gap-2 h-12 rounded-xl border text-sm cursor-pointer transition-all duration-200 ${
                            form.gender === g
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-medium"
                              : "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300"
                          }`}
                        >
                          <RadioGroupItem value={g} id={`gender-${g}`} className="sr-only" />
                          {g}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <Label htmlFor="birth_date" className={labelClass}>Date de naissance <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="birth_date" name="birth_date" type="date" value={form.birth_date} onChange={handleChange} required className={field} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="birth_place" className={labelClass}>Lieu de naissance <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="birth_place" name="birth_place" value={form.birth_place} onChange={handleChange} required className={field} placeholder="Moroni, Comores" />
                  </div>
                </div>
              </div>
            </section>

            <div className="border-t border-slate-100" />

            {/* ── 02. Coordonnées ── */}
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6 flex items-center gap-3">
                <span className="font-serif text-slate-200 text-2xl leading-none">02</span>
                Coordonnées
              </p>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className={labelClass}>Téléphone</Label>
                    <Input
                      id="phone" name="phone" value={form.phone} disabled
                      className={`${field} opacity-50 cursor-not-allowed`}
                    />
                    <p className="text-xs text-slate-400 mt-1">Numéro utilisé pour la connexion.</p>
                  </div>
                  <div>
                    <Label htmlFor="profession" className={labelClass}>Profession</Label>
                    <Input id="profession" name="profession" value={form.profession} onChange={handleChange} className={field} placeholder="Infirmière" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="sm:col-span-2">
                    <Label htmlFor="address_street" className={labelClass}>Adresse <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="address_street" name="address_street" value={form.address_street} onChange={handleChange} required className={field} placeholder="12 rue de la Paix" />
                  </div>
                  <div>
                    <Label htmlFor="address_postal_code" className={labelClass}>Code postal <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="address_postal_code" name="address_postal_code" value={form.address_postal_code} onChange={handleChange} required className={field} placeholder="75001" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="address_city" className={labelClass}>Commune <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="address_city" name="address_city" value={form.address_city} onChange={handleChange} required className={field} placeholder="Paris" />
                  </div>
                  <div>
                    <Label htmlFor="country" className={labelClass}>Pays <span className="text-red-400 normal-case">*</span></Label>
                    <Input id="country" name="country" value={form.country} onChange={handleChange} required className={field} placeholder="FR" />
                  </div>
                </div>
              </div>
            </section>

            <div className="border-t border-slate-100" />

            {/* ── 03. Situation ── */}
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6 flex items-center gap-3">
                <span className="font-serif text-slate-200 text-2xl leading-none">03</span>
                Situation personnelle
              </p>
              <div className="space-y-5">
                <div>
                  <Label className={labelClass}>État civil</Label>
                  <Select
                    value={form.civil_status}
                    onValueChange={(v) => setForm((p) => ({ ...p, civil_status: v }))}
                  >
                    <SelectTrigger className={`${field} w-full`}>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celibataire">Célibataire</SelectItem>
                      <SelectItem value="marie">Marié(e) / Pacsé(e) / Union libre</SelectItem>
                      <SelectItem value="divorce">Divorcé(e)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.civil_status === "marie" && (
                  <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5 space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Conjoint(e)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className={labelClass}>Prénom</Label>
                        <Input name="spouse_first_name" value={form.spouse_first_name} onChange={handleChange} className={field} placeholder="Prénom" />
                      </div>
                      <div>
                        <Label className={labelClass}>Nom</Label>
                        <Input name="spouse_last_name" value={form.spouse_last_name} onChange={handleChange} className={field} placeholder="Nom" />
                      </div>
                      <div>
                        <Label className={labelClass}>Date de naissance</Label>
                        <Input name="spouse_birth_date" type="date" value={form.spouse_birth_date} onChange={handleChange} className={field} />
                      </div>
                      <div>
                        <Label className={labelClass}>Téléphone</Label>
                        <Input name="spouse_phone" value={form.spouse_phone} onChange={handleChange} className={field} placeholder="+33 6 00 00 00 00" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <div className="border-t border-slate-100" />

            {/* ── 04. Enfants ── */}
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6 flex items-center gap-3">
                <span className="font-serif text-slate-200 text-2xl leading-none">04</span>
                Enfants à charge
              </p>

              <div className="flex gap-3 mb-5">
                {["non", "oui"].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setHasChildren(val)}
                    className={`flex-1 h-11 rounded-xl border text-sm font-medium transition-all duration-200 capitalize ${
                      hasChildren === val
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    {val === "oui" ? "Oui, j'ai des enfants de moins de 18 ans" : "Non"}
                  </button>
                ))}
              </div>

              {hasChildren === "oui" && (
                <div className="space-y-3">
                  {children.map((child, index) => (
                    <div key={index} className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
                        Enfant {index + 1}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label className={labelClass}>Prénom</Label>
                          <Input value={child.first_name} onChange={(e) => handleChildChange(index, "first_name", e.target.value)} className={field} placeholder="Prénom" />
                        </div>
                        <div>
                          <Label className={labelClass}>Nom</Label>
                          <Input value={child.last_name} onChange={(e) => handleChildChange(index, "last_name", e.target.value)} className={field} placeholder="Nom" />
                        </div>
                        <div>
                          <Label className={labelClass}>Date de naissance</Label>
                          <Input type="date" value={child.birth_date} onChange={(e) => handleChildChange(index, "birth_date", e.target.value)} className={field} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setChildren((p) => [...p, { first_name: "", last_name: "", birth_date: "" }])}
                    className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800 font-medium transition-colors mt-1"
                  >
                    <Plus className="h-4 w-4" /> Ajouter un enfant
                  </button>
                </div>
              )}
            </section>

            <div className="border-t border-slate-100" />

            {/* ── 05. Bénéficiaire ── */}
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-2 flex items-center gap-3">
                <span className="font-serif text-slate-200 text-2xl leading-none">05</span>
                Bénéficiaire désigné
              </p>
              <p className="text-sm text-slate-400 font-light mb-5">
                Personne à contacter en cas de sinistre.
              </p>

              <div className="flex gap-3 mb-5">
                {["non", "oui"].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setBeneficiaryChoice(val)}
                    className={`flex-1 h-11 rounded-xl border text-sm font-medium transition-all duration-200 ${
                      beneficiaryChoice === val
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    {val === "oui" ? "Oui, désigner un bénéficiaire" : "Non"}
                  </button>
                ))}
              </div>

              {beneficiaryChoice === "oui" && (
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">Bénéficiaire</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className={labelClass}>Prénom</Label>
                      <Input name="first_name" value={beneficiary.first_name} onChange={handleBeneficiaryChange} className={field} placeholder="Prénom" />
                    </div>
                    <div>
                      <Label className={labelClass}>Nom</Label>
                      <Input name="last_name" value={beneficiary.last_name} onChange={handleBeneficiaryChange} className={field} placeholder="Nom" />
                    </div>
                    <div>
                      <Label className={labelClass}>Téléphone</Label>
                      <Input name="phone" value={beneficiary.phone} onChange={handleBeneficiaryChange} className={field} placeholder="+33 6 00 00 00 00" />
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="border-t border-slate-100" />

            {/* ── Validation ── */}
            <section className="space-y-5">
              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                <p className="text-sm text-slate-600 font-light leading-relaxed mb-4">
                  En validant ce formulaire, je reconnais avoir pris connaissance des conditions générales
                  d'adhésion à la communauté Hafani et m'engage à les respecter.
                </p>
                <label htmlFor="accept_cg" className="flex items-start gap-3 cursor-pointer group">
                  <Checkbox
                    id="accept_cg"
                    checked={acceptCG}
                    onCheckedChange={(c) => setAcceptCG(Boolean(c))}
                    className="mt-0.5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                  />
                  <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">
                    J'ai lu et j'accepte les conditions générales d'adhésion.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading || !acceptCG}
                className="w-full bg-slate-900 hover:bg-emerald-800 text-white rounded-full py-6 text-sm font-medium tracking-wide shadow-sm transition-all duration-300 disabled:opacity-50 group"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enregistrement…</>
                ) : (
                  <>Enregistrer et passer au paiement <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>
                )}
              </Button>
            </section>

          </div>
        </form>
      </main>

      <footer className="py-6 text-center border-t border-slate-200/60">
        <p className="text-xs text-slate-400 font-light">
          &copy; {new Date().getFullYear()} Hafani Management and Solutions
        </p>
      </footer>

    </div>
  );
};
